/**
 * Ready-made email signatures. Each design turns a few fields into email-safe
 * HTML: tables and inline styles only, hosted PNG logos at 2x with fixed
 * width/height, no web fonts. {{name}} stays in the output; the API fills it
 * with whoever sends.
 *
 * The fields ride along in an HTML comment so the form can reopen a saved
 * signature. Mail clients ignore comments.
 */

export interface SignatureFields {
  design: SignatureDesignId;
  team: string;
  email: string;
  phone: string;
  whatsapp: string;
  website: string;
  tagline: string;
  instagram: string;
  facebook: string;
  x: string;
  linkedin: string;
  // Added with the banner design; signatures saved before it lack them.
  location?: string;
  youtube?: string;
  tiktok?: string;
  handle?: string;
}

export type SignatureDesignId = 'banner' | 'brand' | 'mark' | 'compact';

const ASSETS = 'https://feelyfeely.com/assets/brand/email';
const LOGO = `${ASSETS}/feelyfeely-logo-horizontal@2x.png`; // 336x72, shown at 168x36
const MARK = `${ASSETS}/feelyfeely-mark@2x.png`; // 112x112, shown at 56x56

const NAVY = '#1C295E';
const BLUE = '#0E32BC';
const PURPLE = '#5A1BC2';
const GREY = '#5B6475';
const FONT = 'font-family:Arial,Helvetica,sans-serif;';

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const href = (url: string) => esc(/^https?:\/\//i.test(url) ? url : `https://${url}`);
const bare = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
const digits = (phone: string) => phone.replace(/[^\d+]/g, '');

const link = (url: string, text: string, color = BLUE) =>
  `<a href="${url}" style="color:${color};text-decoration:none;">${esc(text)}</a>`;

/** Contact lines as "label value" pairs, skipping empty fields. */
function contacts(f: SignatureFields): string[] {
  return [
    f.phone && link(`tel:${digits(f.phone)}`, f.phone, GREY),
    f.whatsapp && `WhatsApp ${link(`https://wa.me/${digits(f.whatsapp).replace('+', '')}`, f.whatsapp, GREY)}`,
    f.email && link(`mailto:${esc(f.email)}`, f.email, GREY),
    f.website && link(href(f.website), bare(f.website)),
  ].filter(Boolean) as string[];
}

function socials(f: SignatureFields): string {
  const items = (
    [
      ['Instagram', f.instagram],
      ['Facebook', f.facebook],
      ['X', f.x],
      ['LinkedIn', f.linkedin],
    ] as const
  ).filter(([, url]) => url);
  return items.map(([label, url]) => link(href(url), label, PURPLE)).join(' &nbsp;&middot;&nbsp; ');
}

const NAME = `<span style="${FONT}font-size:15px;font-weight:bold;color:${NAVY};">{{name}}</span>`;

function brand(f: SignatureFields): string {
  const lines = contacts(f);
  const social = socials(f);
  return `
<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;${FONT}">
  <tr><td style="padding:0 0 2px 0;">${NAME}</td></tr>
  ${f.team ? `<tr><td style="${FONT}font-size:13px;color:${GREY};padding:0 0 10px 0;">${esc(f.team)}</td></tr>` : ''}
  <tr><td style="padding:0 0 10px 0;"><table cellpadding="0" cellspacing="0" border="0" role="presentation"><tr>
    <td width="40" height="3" style="background:${BLUE};font-size:0;line-height:0;">&nbsp;</td>
    <td width="40" height="3" style="background:${PURPLE};font-size:0;line-height:0;">&nbsp;</td>
  </tr></table></td></tr>
  <tr><td style="padding:0 0 10px 0;"><a href="https://feelyfeely.com" style="text-decoration:none;"><img src="${LOGO}" width="168" height="36" alt="FeelyFeely" style="display:block;border:0;width:168px;height:36px;"></a></td></tr>
  ${lines.length ? `<tr><td style="${FONT}font-size:13px;line-height:20px;color:${GREY};">${lines.join(' &nbsp;|&nbsp; ')}</td></tr>` : ''}
  ${social ? `<tr><td style="${FONT}font-size:12px;line-height:20px;padding:4px 0 0 0;">${social}</td></tr>` : ''}
  ${f.tagline ? `<tr><td style="${FONT}font-size:12px;font-style:italic;color:${GREY};padding:8px 0 0 0;">${esc(f.tagline)}</td></tr>` : ''}
</table>`.trim();
}

function mark(f: SignatureFields): string {
  const lines = contacts(f);
  const social = socials(f);
  return `
<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;${FONT}">
  <tr>
    <td valign="top" style="padding:2px 16px 0 0;"><a href="https://feelyfeely.com" style="text-decoration:none;"><img src="${MARK}" width="56" height="56" alt="FeelyFeely" style="display:block;border:0;width:56px;height:56px;"></a></td>
    <td valign="top" style="border-left:2px solid ${PURPLE};padding:0 0 0 16px;">
      <div style="${FONT}">${NAME}</div>
      <div style="${FONT}font-size:13px;color:${BLUE};font-weight:bold;padding:2px 0 6px 0;">${esc(f.team || 'FeelyFeely')}</div>
      ${lines.map((l) => `<div style="${FONT}font-size:13px;line-height:19px;color:${GREY};">${l}</div>`).join('')}
      ${social ? `<div style="${FONT}font-size:12px;line-height:19px;padding:4px 0 0 0;">${social}</div>` : ''}
      ${f.tagline ? `<div style="${FONT}font-size:12px;font-style:italic;color:${GREY};padding:6px 0 0 0;">${esc(f.tagline)}</div>` : ''}
    </td>
  </tr>
</table>`.trim();
}

/**
 * The design team's banner: white text on the blue-purple fingerprint
 * background, then a white strip with the logo, tagline and social icons.
 * Outlook desktop ignores background images and shows the flat blue instead.
 */
function banner(f: SignatureFields): string {
  const white = (url: string, text: string) => link(url, text, '#FFFFFF');
  const rows = [
    f.phone && ['phone', white(`tel:${digits(f.phone)}`, f.phone)],
    f.whatsapp && ['phone', `WhatsApp ${white(`https://wa.me/${digits(f.whatsapp).replace('+', '')}`, f.whatsapp)}`],
    f.email && ['email', white(`mailto:${esc(f.email)}`, f.email)],
    f.website && ['web', white(href(f.website), bare(f.website))],
    f.location && ['location', esc(f.location)],
  ].filter(Boolean) as [string, string][];
  const icons = (
    [
      ['instagram', f.instagram],
      ['facebook', f.facebook],
      ['x', f.x],
      ['linkedin', f.linkedin],
      ['youtube', f.youtube],
      ['tiktok', f.tiktok],
    ] as const
  )
    .filter(([, url]) => url)
    .map(
      ([n, url]) =>
        `<td style="padding:0 0 0 4px;"><a href="${href(url!)}" style="text-decoration:none;"><img src="${ASSETS}/social-${n}@2x.png" width="24" height="24" alt="${n}" style="display:block;border:0;width:24px;height:24px;"></a></td>`,
    )
    .join('');
  const BG = `${ASSETS}/signature-banner@2x.png`;
  return `
<table width="600" cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;width:100%;max-width:600px;${FONT}">
  <tr><td bgcolor="#183097" background="${BG}" style="background-color:#183097;background-image:url('${BG}');background-position:right center;background-size:cover;background-repeat:no-repeat;padding:28px 32px;">
    <div style="${FONT}font-size:26px;line-height:30px;font-weight:bold;color:#FFFFFF;">{{name}}</div>
    ${f.team ? `<div style="${FONT}font-size:16px;line-height:22px;color:#FFFFFF;">${esc(f.team)}</div>` : ''}
    ${rows.length ? `<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;margin-top:16px;">${rows
      .map(
        ([icon, text]) =>
          `<tr><td width="22" style="padding:4px 10px 4px 0;"><img src="${ASSETS}/icon-${icon}@2x.png" width="22" height="22" alt="" style="display:block;border:0;width:22px;height:22px;"></td><td style="${FONT}font-size:14px;line-height:22px;color:#FFFFFF;padding:4px 0;">${text}</td></tr>`,
      )
      .join('')}</table>` : ''}
  </td></tr>
  <tr><td bgcolor="#FFFFFF" style="background-color:#FFFFFF;padding:14px 32px;"><table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;"><tr>
    <td valign="middle"><a href="https://feelyfeely.com" style="text-decoration:none;"><img src="${LOGO}" width="112" height="24" alt="FeelyFeely" style="display:block;border:0;width:112px;height:24px;"></a>
      ${f.tagline ? `<div style="${FONT}font-size:12px;line-height:16px;font-weight:bold;color:${NAVY};padding:6px 0 0 0;">${esc(f.tagline)}</div>` : ''}</td>
    ${icons || f.handle ? `<td valign="middle" align="right"><table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;"><tr>${icons}${f.handle ? `<td style="${FONT}font-size:13px;color:${BLUE};padding:0 0 0 6px;white-space:nowrap;">${esc(f.handle)}</td>` : ''}</tr></table></td>` : ''}
  </tr></table></td></tr>
</table>`.trim();
}

function compact(f: SignatureFields): string {
  const lines = contacts(f);
  return `
<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;${FONT}">
  <tr>
    <td valign="middle" style="padding:0 10px 0 0;"><img src="${MARK}" width="28" height="28" alt="FeelyFeely" style="display:block;border:0;width:28px;height:28px;"></td>
    <td valign="middle" style="${FONT}font-size:13px;line-height:18px;color:${GREY};">
      ${NAME}${f.team ? ` &nbsp;&middot;&nbsp; <span style="color:${NAVY};">${esc(f.team)}</span>` : ''}<br>
      ${lines.join(' &nbsp;&middot;&nbsp; ')}
    </td>
  </tr>
</table>`.trim();
}

export const SIGNATURE_DESIGNS: { id: SignatureDesignId; label: string; render: (f: SignatureFields) => string }[] = [
  { id: 'banner', label: 'Banner', render: banner },
  { id: 'brand', label: 'Logo', render: brand },
  { id: 'mark', label: 'Side mark', render: mark },
  { id: 'compact', label: 'Compact', render: compact },
];

export const defaultFields = (mailbox: { email: string; name?: string }): SignatureFields => ({
  design: 'banner',
  team: mailbox.name || 'FeelyFeely',
  email: mailbox.email,
  phone: '',
  whatsapp: '',
  website: 'feelyfeely.com',
  tagline: 'Ghana’s trust-first intelligent marketplace',
  instagram: '',
  facebook: '',
  x: '',
  linkedin: '',
  location: 'Accra, Ghana',
  youtube: '',
  tiktok: '',
  handle: '@feelyfeelygh',
});

const MARKER = /<!--ffsig:([A-Za-z0-9+/=]+)-->/;

export function renderSignature(f: SignatureFields): string {
  const design = SIGNATURE_DESIGNS.find((d) => d.id === f.design) ?? SIGNATURE_DESIGNS[0];
  const state = btoa(unescape(encodeURIComponent(JSON.stringify(f))));
  return `<!--ffsig:${state}-->\n${design.render(f)}`;
}

/** The fields a signature was built from, or null for hand-written HTML. */
export function readFields(html: string): SignatureFields | null {
  const m = html.match(MARKER);
  if (!m) return null;
  try {
    return JSON.parse(decodeURIComponent(escape(atob(m[1]))));
  } catch {
    return null;
  }
}
